<template>
  <div>
    <v-btn fab dark color="primary" small @click="exportExtract">
      <v-icon>get_app</v-icon>
    </v-btn>
    
    <!-- Export Status Modal -->
    <v-dialog v-model="showExportModal" max-width="500">
      <v-card>
        <v-card-title>
          <span class="headline">Exportação de Extrato</span>
        </v-card-title>
        <v-card-text>
          <div v-if="exportStatus === 'processing'">
            <v-progress-circular indeterminate color="primary" class="mr-3"></v-progress-circular>
            <span>Gerando relatório... Aguarde alguns instantes.</span>
          </div>
          <div v-else-if="exportStatus === 'ready'">
            <v-icon color="success" class="mr-3">check_circle</v-icon>
            <span>Relatório pronto para download!</span>
            <br><br>
            <strong>Arquivo:</strong> {{ exportFileName || 'Aguardando...' }}
          </div>
          <div v-else-if="exportStatus === 'error'">
            <v-icon color="error" class="mr-3">error</v-icon>
            <span>Erro ao gerar relatório. Tente novamente.</span>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="closeExportModal">
            Fechar
          </v-btn>
          <v-btn 
            v-if="exportStatus === 'ready' && exportFileName" 
            color="primary" 
            @click="downloadFileManual"
            :disabled="!exportFileName"
          >
            <v-icon left>get_app</v-icon>
            Download
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ActionCable from "actioncable";

export default {
  name: "ExtractReport",
  data() {
    return {
      downloadUrl: "",
      exportFileName: null,
      snackbar: false,
      showExportModal: false,
      exportStatus: 'processing', // 'processing', 'ready', 'error'
      exportInProgress: false,
      subscription: null,
    };
  },
  computed: {
    ...mapGetters({
      filterParams: "wallet/filterParams",
      systemAppName: "system/appName",
    }),
  },
  methods: {
    exportExtract() {
      // Prevent multiple exports
      if (this.exportInProgress) {
        return;
      }
      
      this.exportInProgress = true;
      this.showExportModal = true;
      this.exportStatus = 'processing';
      this.downloadUrl = null;
      this.exportFileName = null;
      
      // Use mutation instead of direct state modification
      this.$store.commit("wallet/SET_FILTER_INITIAL_DATE", 
        this.$store.state.wallet.filterParams.filterrific.created_at);
      this.$store.dispatch("wallet/exportWallet", this.filterParams);
      
      // Start polling as fallback after 3 seconds
      setTimeout(() => {
        if (this.exportStatus === 'processing') {
          console.log('Starting polling fallback for export');
          this.startPollingForFile();
        }
      }, 3000);
    },
    
    closeExportModal() {
      this.showExportModal = false;
      this.exportInProgress = false;
      this.downloadUrl = null;
      this.exportFileName = null;
    },
    
    downloadFileManual() {
      if (this.exportFileName) {
        this.$store.dispatch('wallet/downloadWalletExport', { file_name: this.exportFileName })
          .then(() => {
            this.closeExportModal();
          })
          .catch(error => {
            console.error('Error downloading file:', error);
            this.exportStatus = 'error';
          });
      }
    },
    
    startPollingForFile() {
      // Poll the backend to check if export is ready
      let attempts = 0;
      const maxAttempts = 30; // 30 attempts = 60 seconds total
      
      const pollInterval = setInterval(() => {
        attempts++;
        console.log(`Polling attempt ${attempts}/${maxAttempts}`);
        
        if (attempts >= maxAttempts) {
          clearInterval(pollInterval);
          this.exportStatus = 'error';
          this.exportInProgress = false;
          console.warn('Polling timeout - export not ready');
          return;
        }
        
        this.checkWalletExportStatus(pollInterval);
      }, 2000); // Poll every 2 seconds
    },
    
    checkWalletExportStatus(pollInterval = null) {
      this.$store.dispatch('wallet/checkWalletExportStatus')
        .then((response) => {
          console.log('Export status:', response.data);
          
          if (response.data.status === 'completed' && response.data.file_name) {
            console.log('Export completed via polling!', response.data.file_name);
            if (pollInterval) clearInterval(pollInterval);
            this.exportStatus = 'ready';
            this.exportFileName = response.data.file_name;
            this.exportInProgress = false;
          }
        })
        .catch(error => {
          console.log('Polling check failed, continuing...', error.message);
        });
    },
  },
  created() {
    this.$store.dispatch('system/fetchSystemConfiguration');
    
    // Get token from sessionStorage
    const appName = this.systemAppName || process.env.VUE_APP_APP_NAME || 'flowit';
    const token = window.sessionStorage.getItem(`${appName}-session`);
    
    // Only create cable connection if token exists
    if (token) {
      const cable = ActionCable.createConsumer(
        `${process.env.VUE_APP_WEBSOCKET_PROTOCOL}://${
          process.env.VUE_APP_FLOWIT_URL
        }/cable?token=${token}`
      );
      
      /* eslint-disable */
      this.subscription = cable.subscriptions.create(
        {
          channel: "WebNotificationsChannel",
        },
        {
          connected: () => {
            console.log('WebSocket connected for wallet export');
          },
          disconnected: () => {
            console.log('WebSocket disconnected for wallet export');
            // Don't set error immediately, the worker might still be running
            // The user can close and retry if needed
          },
          rejected: () => {
            console.log('WebSocket rejected for wallet export');
            this.exportStatus = 'error';
            this.exportInProgress = false;
          },
          received: (data) => {
            console.log('WebSocket data received:', data);
            if (data.event == "UserReporter.finished") {
              console.log('Export finished:', data.body);
              if (data.body && data.body.file_name) {
                this.exportStatus = 'ready';
                this.exportFileName = data.body.file_name;
                this.exportInProgress = false;
              }
            } else if (data.event == "UserReporter.error") {
              console.log('Export error received');
              this.exportStatus = 'error';
              this.exportInProgress = false;
            }
          },
        }
      );
    } else {
      console.warn('No session token found for ActionCable connection');
      this.exportStatus = 'error';
      this.exportInProgress = false;
    }
  },
  
  beforeDestroy() {
    // Clean up subscription when component is destroyed
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  },
};
</script>